<script setup lang="ts">
/**
 * « Cette alerte était-elle utile ? » sur le détail d'un écart inhabituel,
 * connecté (geste 40 `episode_avis_utilite`, route 18 ; `questionUtile`
 * S:7620-7634, D108).
 *
 * Ce que l'utilisateur doit vivre : il répond Oui ou Non ; la réponse ne
 * s'affiche « enregistrée » qu'une fois le serveur l'a rendue, et reste
 * modifiable. Redonner la même réponse n'écrit rien de plus côté serveur :
 * après un délai dépassé, répondre de nouveau est donc sans danger, et
 * l'écran le dit. La question ne se pose que sur un écart inhabituel ; un
 * refus (pas destinataire, pas un écart) se dit par sa phrase.
 */
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import {
    cibleAvis,
    useActionsStore,
} from "@modules/alarmes-v2/stores/actions.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import type {
    AlarmesV2Alarme,
    AlarmesV2Avis,
    AlarmesV2ValeurAvis,
} from "@modules/alarmes-v2/types/alarmes-v2"
import type { GesteDeRangee } from "./RangeeGestes.vue"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** L'alarme du détail. */
    alarme: AlarmesV2Alarme
}>()

/**
 * Le préfixe des codes des types « écart inhabituel » (T1 §7.5,
 * `ecart_inhabituel_electricite`, `ecart_inhabituel_gaz`). [NON VÉRIFIÉ] sur
 * un type servi : aucun n'est publié au 05/10 (P25).
 */
const PREFIXE_ECART = "ecart_inhabituel"

const actions = useActionsStore()
const { acces } = useAcces()

/** Vrai quand la personne veut changer sa réponse. */
const changer = ref(false)

// ================================
// COMPUTED
// ================================
const getEcart = computed(
    () =>
        typeof props.alarme.type_code === "string" &&
        props.alarme.type_code.startsWith(PREFIXE_ECART)
)

const getCible = computed(() => cibleAvis(props.alarme._id))
const getEtat = computed(() => actions.etat(getCible.value))

/** La réponse enregistrée, rendue par le serveur, ou `null`. */
const getEnregistree = computed((): AlarmesV2ValeurAvis | null => {
    const e = getEtat.value
    if (!e || e.statut !== "fait" || e.geste !== "avis") return null
    const v = (e.valeur as AlarmesV2Avis | null)?.valeur
    return v === "utile" || v === "pas_utile" ? v : null
})

const getAcces = computed(() => acces("episode_avis_utilite"))

const getGestes = computed((): GesteDeRangee[] => [
    {
        acces: { ...getAcces.value, cle: "utile" },
        libelle: t("alarmesV2.actions.avis.oui"),
        classe: "rounded border border-surface-300 px-3 py-1 text-sm",
    },
    {
        acces: { ...getAcces.value, cle: "pas_utile" },
        libelle: t("alarmesV2.actions.avis.non"),
        classe: "rounded border border-surface-300 px-3 py-1 text-sm",
    },
])

const getTexteFait = computed(() => {
    const v = getEnregistree.value
    if (!v) return ""
    return t("alarmesV2.actions.fait.avis", {
        valeur:
            v === "utile"
                ? t("alarmesV2.actions.avis.utile")
                : t("alarmesV2.actions.avis.pasUtile"),
    })
})

// ================================
// HANDLERS
// ================================
/** Rien à relire : l'avis n'est pas servi ailleurs ; la réponse fait foi. */
const relire = async (): Promise<boolean> => true

async function onRepondre(cle: string): Promise<void> {
    if (cle !== "utile" && cle !== "pas_utile") return
    const id = props.alarme._id
    const issue = await actions.ecrire({
        cible: cibleAvis(id),
        geste: "avis",
        appel: () => serviceAlarmesV2.alarmesV2DonnerAvis(id, cle),
        relire,
    })
    if (issue.issue === "fait") changer.value = false
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <section
        v-if="getEcart && getAcces.affichage !== 'absent'"
        class="flex flex-col gap-2"
        data-test="avis-utilite"
    >
        <template v-if="getEnregistree && !changer">
            <AnnonceEcriture
                :cible="getCible"
                :texte-fait="getTexteFait"
                :relire="relire"
            />
            <button
                type="button"
                class="self-start text-sm text-primary-700 underline"
                data-test="changer-avis"
                @click="changer = true"
            >
                {{ t("alarmesV2.actions.avis.changer") }}
            </button>
        </template>
        <template v-else>
            <span class="text-sm font-semibold">{{
                t("alarmesV2.actions.avis.question")
            }}</span>
            <RangeeGestes :gestes="getGestes" @geste="onRepondre" />
            <AnnonceEcriture
                v-if="getEtat"
                :cible="getCible"
                :texte-incertain="t('alarmesV2.actions.avis.incertain')"
                :relire="relire"
            />
        </template>
    </section>
</template>
